import { useSearchParams } from "react-router";
import { consoleApi, type ConsoleAnalyticsRange } from "../../../lib/admin-console-api";
import { useAppPreferences } from "../../../lib/preferences";
import { LoadState, PageHeader, Section, useFormatters, useLoad } from "../ui";

const RANGES: Array<{ id: ConsoleAnalyticsRange; label: string }> = [
  { id: "7d", label: "7 days" },
  { id: "30d", label: "30 days" },
  { id: "90d", label: "90 days" },
  { id: "all", label: "All" },
];

const TREND_LABELS: Record<string, string> = {
  bookings_count: "Bookings",
  cancellations_count: "Cancellations",
  no_show_count: "No-shows",
};

/** Cohort stages: accounts registered in the window, then booked, then played. */
const FUNNEL_STAGE_LABELS: Record<string, string> = {
  registrations: "Accounts registered",
  registered: "Accounts registered",
  first_booking: "Booked",
  first_session: "Played",
};

function Bars({ buckets }: { buckets: Array<{ date: string; value: number }> }) {
  const { dateOnly } = useFormatters();
  const max = Math.max(1, ...buckets.map((bucket) => bucket.value));
  return (
    <div className="console-bars" role="img" aria-label={buckets.map((bucket) => `${dateOnly(bucket.date)}: ${bucket.value}`).join(", ")}>
      {buckets.map((bucket) => (
        <span className="console-bar" key={bucket.date} style={{ height: `${Math.max(4, (bucket.value / max) * 100)}%` }} title={`${dateOnly(bucket.date)}: ${bucket.value}`} />
      ))}
    </div>
  );
}

export function AnalyticsPage() {
  const { t } = useAppPreferences();
  const [params, setParams] = useSearchParams();
  const range = RANGES.find((item) => item.id === params.get("range")) ?? RANGES[1]!;
  const { dateOnly, dateTime } = useFormatters();
  // One network-only snapshot per range. No polling, no fan-out: every panel
  // below reads this same payload, so a slow superseded response can never
  // mix panels from two different ranges (useLoad ignores late arrivals).
  const report = useLoad(() => consoleApi.analyticsReport(range.id), [range.id]);
  const data = report.data;
  const expired = data ? Number.isFinite(Date.parse(data.expires_at)) && Date.now() > Date.parse(data.expires_at) : false;

  const tiles = data
    ? [
        [t("Approved providers"), data.totals.approved_providers],
        [t("Live venues"), data.totals.approved_venues],
        [t("Bookings on snapshot date"), data.totals.today_bookings],
        [t("Active sessions at snapshot"), data.totals.active_sessions],
        [t("Suspended providers"), data.totals.suspended_providers],
      ]
    : [];

  const asOf = !data
    ? report.error
      ? t("Snapshot unavailable.")
      : t("Loading snapshot...")
    : expired
      ? t("Snapshot expired {date}. Refresh for the latest snapshot.", { date: dateTime(data.expires_at) })
      : t("As of {date}. Cached for one hour; refresh after expiry.", { date: dateTime(data.generated_at) });

  return (
    <div className="console-page">
      <PageHeader
        actions={
          <>
            <div className="console-pills">
              {RANGES.map((item) => (
                <button aria-pressed={item.id === range.id} className="ops-filter-pill" data-active={item.id === range.id} key={item.id} onClick={() => setParams({ range: item.id })} type="button">
                  {t(item.label)}
                </button>
              ))}
            </div>
            {data ? (
              <button className="ops-button" data-size="sm" data-variant="secondary" disabled={report.loading} onClick={() => void report.reload()} type="button">
                {t("Refresh")}
              </button>
            ) : null}
          </>
        }
        description={t("Platform-wide numbers. Venue-level detail lives in each provider's workspace.")}
        kicker={t("Platform")}
        title={t("Analytics")}
      />
      <p aria-live="polite" className="console-muted" role="status">{asOf}</p>
      <Section
        description={t("Platform counts are a snapshot, not live. The date range applies to the reports below.")}
        title={t("Platform snapshot")}
      >
        <LoadState error={report.error} isEmpty={!data} loading={report.loading} onRetry={() => void report.reload()}>
          <div className="console-card-grid">
            {tiles.map(([label, value]) => (
              <div className="ops-stat-card" key={String(label)}>
                <p className="ops-stat-label">{label}</p>
                <p className="ops-stat-value">{Number(value).toLocaleString()}</p>
              </div>
            ))}
          </div>
        </LoadState>
      </Section>
      <Section title={t("Trends")}>
        <LoadState error={report.error} isEmpty={!data} loading={report.loading} onRetry={() => void report.reload()}>
          <div className="console-grid-3">
            {(data?.trends ?? []).map((item) => (
              <div className="ops-subtle-surface console-chart" key={item.metric}>
                <p className="ops-label">{t(TREND_LABELS[item.metric] ?? item.metric)}</p>
                <p className="console-chart-total">{item.buckets.reduce((sum, bucket) => sum + bucket.value, 0).toLocaleString()}</p>
                <Bars buckets={item.buckets} />
              </div>
            ))}
          </div>
        </LoadState>
      </Section>
      <div className="console-grid-2">
        <Section title={t("Busiest venues")}>
          <LoadState error={report.error} isEmpty={!data} loading={report.loading} onRetry={() => void report.reload()}>
            <ol className="console-ranking">
              {(data?.rankings.venues.items ?? []).map((item) => (
                <li key={item.id}><span>{item.name}</span><strong>{item.value.toLocaleString()}</strong></li>
              ))}
            </ol>
          </LoadState>
        </Section>
        <Section title={t("Busiest providers")}>
          <LoadState error={report.error} isEmpty={!data} loading={report.loading} onRetry={() => void report.reload()}>
            <ol className="console-ranking">
              {(data?.rankings.providers.items ?? []).map((item) => (
                <li key={item.id}><span>{item.name}</span><strong>{item.value.toLocaleString()}</strong></li>
              ))}
            </ol>
          </LoadState>
        </Section>
      </div>
      <Section
        description={
          !data
            ? t("How bookings progressed in the selected period.")
            : data.from_date === null
              ? t("Full account history to {date}.", { date: dateOnly(data.funnel.to_date) })
              : t("Accounts registered {from} to {to}.", { from: dateOnly(data.funnel.from_date), to: dateOnly(data.funnel.to_date) })
        }
        title={t("Booking funnel")}
      >
        <LoadState error={report.error} isEmpty={!data} loading={report.loading} onRetry={() => void report.reload()}>
          <p className="console-muted">
            {t("Counts accounts registered in this period, then how many booked, then how many played through a booking-linked session. Walk-ins without a booking are not counted.")}
          </p>
          <ol className="console-funnel">
            {(data?.funnel.stages ?? []).map((stage) => (
              <li key={stage.name}>
                <span>{t(FUNNEL_STAGE_LABELS[stage.name] ?? stage.name.replaceAll("_", " "))}</span>
                <strong>{stage.count.toLocaleString()}</strong>
                {stage.rate !== null ? <span className="console-muted">{Math.round(stage.rate * 100)}%</span> : null}
              </li>
            ))}
          </ol>
        </LoadState>
      </Section>
    </div>
  );
}
